import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import DashboardHeader from './components/DashboardHeader';
import PostListTab from './components/PostListTab';
import PendingPostsTab from './components/PendingPostsTab';
import UrgentAlertTab from './components/UrgentAlertTab';
import ReplyHistoryTab from './components/ReplyHistoryTab';
import ShiftModal from './components/ShiftModal';

type TabType = 'all' | 'pending' | 'urgent' | 'history';

export default function PartnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [replies, setReplies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [stats, setStats] = useState({
    totalReplies: 0,
    weeklyReplies: 0,
    empathyReceived: 0,
    responseRate: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    const userType = user.user_metadata?.user_type;
    if (userType !== 'partner') {
      navigate('/board');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data: postsData } = await supabase
        .from('posts')
        .select(`
          *,
          profiles:user_id (
            nickname,
            user_type,
            partner_role,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      const { data: repliesData } = await supabase
        .from('replies')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setPosts(postsData || []);
      setReplies(repliesData || []);

      const totalReplies = repliesData?.length || 0;
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - 7);
      const weeklyReplies = repliesData?.filter(r => new Date(r.created_at) >= weekStart).length || 0;

      const { data: empathyData } = await supabase
        .from('empathy')
        .select('id')
        .eq('target_type', 'reply')
        .in('target_id', repliesData?.map(r => r.id) || []);

      const empathyReceived = empathyData?.length || 0;

      const pendingCount = postsData?.filter((p: any) => p.need_partner_response === true).length || 0;
      const totalPosts = postsData?.length || 0;
      const responseRate = totalPosts > 0 ? Math.round(((totalPosts - pendingCount) / totalPosts) * 100) : 0;

      setStats({
        totalReplies,
        weeklyReplies,
        empathyReceived,
        responseRate,
      });
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      await supabase.from('replies').delete().eq('id', replyId);
      setReplies(replies.filter(r => r.id !== replyId));
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  const repliesWithPostInfo = replies.map(reply => {
    const post = posts.find(p => p.id === reply.post_id);
    return {
      ...reply,
      post_title: post?.title || '投稿が見つかりません',
      post_category: post?.category || '',
      post_author_nickname: post?.profiles?.nickname || '匿名',
      post_author_avatar: post?.profiles?.avatar_url,
    };
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50">
      <DashboardHeader stats={stats} onShiftClick={() => setShowShiftModal(true)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* タブナビゲーション */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-6 sm:mb-8 overflow-x-auto">
          <div className="flex gap-2 min-w-max sm:min-w-0">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'all'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-list-check text-base sm:text-lg mr-1 sm:mr-2"></i>
              すべての投稿
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'pending'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-user-star-line text-base sm:text-lg mr-1 sm:mr-2"></i>
              回答待ち
            </button>
            <button
              onClick={() => setActiveTab('urgent')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'urgent'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-alarm-warning-line text-base sm:text-lg mr-1 sm:mr-2"></i>
              緊急アラート
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'history'
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-history-line text-base sm:text-lg mr-1 sm:mr-2"></i>
              回答履歴
            </button>
          </div>
        </div>

        {/* タブコンテンツ */}
        {activeTab === 'all' && (
          <PostListTab
            posts={posts}
            loading={loading}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}
        {activeTab === 'pending' && <PendingPostsTab posts={posts} loading={loading} />}
        {activeTab === 'urgent' && <UrgentAlertTab posts={posts} loading={loading} />}
        {activeTab === 'history' && (
          <ReplyHistoryTab
            replies={repliesWithPostInfo}
            loading={loading}
            onDeleteReply={handleDeleteReply}
          />
        )}
      </div>

      <ShiftModal show={showShiftModal} onClose={() => setShowShiftModal(false)} />
    </div>
  );
}
